import React from "react";

import { ColDef } from "@ag-grid-community/core";
import { FormattedMessage, useIntl } from "react-intl";

import { AnyEntity } from "@mapmycustomers/shared/types/entity";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";
import { Alert } from "@mapmycustomers/ui";

import ActivityAnnotationLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ActivityAnnotationLinkCellRenderer";
import ColorCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ColorCellRenderer";
import CompaniesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CompaniesCellRenderer";
import CountryCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CountryCellRenderer";
import EditableNoteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/EditableNoteCellRenderer";
import EmailCellRenderer from "@app/component/grid/gridComponents/cellRenderers/EmailCellRenderer";
import FrequencyCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyCellRenderer";
import FrequencyDaysLeftCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyDaysLeftCellRenderer";
import FrequencyIntervalCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyIntervalCellRenderer";
import GroupsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/GroupsCellRenderer";
import LocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/LocationCellRenderer";
import MultiOptionCustomFieldCellRenderer from "@app/component/grid/gridComponents/cellRenderers/MultiOptionCustomFieldCellRenderer";
import NotesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NotesCellRenderer";
import PhoneCellRenderer from "@app/component/grid/gridComponents/cellRenderers/PhoneCellRenderer";
import RoutesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/RoutesCellRenderer";
import StageCellRenderer from "@app/component/grid/gridComponents/cellRenderers/StageCellRenderer";
import TeamsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TeamsCellRenderer";
import TerritoriesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TerritoriesCellRenderer";
import TripLocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripLocationCellRenderer";
import TripTypeCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TripTypeCellRenderer";
import UserChipCellRenderer from "@app/component/grid/gridComponents/cellRenderers/UserChipCellRenderer";
import WebsiteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/WebsiteCellRenderer";
import AgGridListView from "@app/component/view/ListView/AgGridListView";
import AgGridListViewServerSideGrid from "@app/component/view/ListView/AgGridListViewServerSideGrid";
import Report from "@app/types/Report";
import { ListFetcherPayload } from "@app/types/viewModel/ListFetcher";
import { EXPORT_RECORD_COUNT_LIMIT } from "@app/util/consts";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import styles from "./PreviewGrid.module.scss";
import UserNameCellRenderer from "./UserNameCellRenderer";

interface Props {
  className?: string;
  columnDefs: ColDef[];
  previewFetcher: (payload: ListFetcherPayload<AnyEntity>) => void;
  previewViewState: ViewState;
  report: Partial<Report>;
  reportTotalRowCount: number;
  toolbar?: React.ReactNode;
}

const PreviewGrid: React.FC<Props> = ({
  className,
  columnDefs,
  previewFetcher,
  previewViewState,
  report,
  reportTotalRowCount,
  toolbar,
}) => {
  const intl = useIntl();

  return (
    <AgGridListView<AnyEntity>
      className={className}
      columnDefs={columnDefs}
      entityType={report.tableName}
      fieldModel={getFieldModelByEntityType(report.tableName!)}
      key={report.tableName}
      listFetcher={previewFetcher}
      listViewState={previewViewState}
    >
      {toolbar}
      {reportTotalRowCount > EXPORT_RECORD_COUNT_LIMIT && (
        <Alert
          className={styles.recordCountWarning}
          message={
            <FormattedMessage
              id="reports.exports.recordCountWarning"
              defaultMessage="This export exceeds the limit of {limit} {limits, plural, one {record} other {records}}. If exported, the file will not be processed until 12:00 AM (Midnight) ET. Apply filters to decrease the size of this export."
              description="Record count warning on export create/edit view"
              values={{
                limit: intl.formatNumber(EXPORT_RECORD_COUNT_LIMIT),
              }}
            />
          }
          showIcon
          type="warning"
        />
      )}
      <section className={styles.gridContainer}>
        <AgGridListViewServerSideGrid
          enableCellTextSelection // Note: When this is set to true, the clipboard service is disabled.
          frameworkComponents={{
            activityAnnotationLinkCellRenderer: ActivityAnnotationLinkCellRenderer,
            colorCellRenderer: ColorCellRenderer,
            companiesCellRenderer: CompaniesCellRenderer,
            countryCellRenderer: CountryCellRenderer,
            editableNoteCellRenderer: EditableNoteCellRenderer,
            emailCellRenderer: EmailCellRenderer,
            frequencyCellRenderer: FrequencyCellRenderer,
            frequencyDaysLeftCellRenderer: FrequencyDaysLeftCellRenderer,
            frequencyIntervalCellRenderer: FrequencyIntervalCellRenderer,
            groupsCellRenderer: GroupsCellRenderer,
            locationCellRenderer: LocationCellRenderer,
            multiOptionCustomFieldCellRenderer: MultiOptionCustomFieldCellRenderer,
            notesCellRenderer: NotesCellRenderer,
            phoneCellRenderer: PhoneCellRenderer,
            routesCellRenderer: RoutesCellRenderer,
            stageCellRenderer: StageCellRenderer,
            teamsCellRenderer: TeamsCellRenderer,
            territoriesCellRenderer: TerritoriesCellRenderer,
            tripLocationCellRenderer: TripLocationCellRenderer,
            tripTypeCellRenderer: TripTypeCellRenderer,
            userCellRenderer: UserNameCellRenderer,
            userChipCellRenderer: UserChipCellRenderer,
            websiteCellRenderer: WebsiteCellRenderer,
          }}
          pagination={false}
        />
      </section>
      <section className={styles.footer}>
        <FormattedMessage
          id="reports.exports.edit.footer"
          defaultMessage="Total records in this export: {count}"
          description="Footer text on the edit report page"
          values={{
            count: reportTotalRowCount,
          }}
        />
      </section>
    </AgGridListView>
  );
};

export default PreviewGrid;
